// The browser half of Kratos's self-service API. The portal shares Kratos's parent domain, so its cookies
// (CSRF and session) travel with credentialed fetches and Kratos answers in JSON instead of redirecting.
import { config } from "./config";

export type FlowType = "login" | "registration" | "recovery" | "verification" | "settings";

export interface UiText {
  id: number;
  text: string;
  type: "info" | "error" | "success";
  context?: Record<string, unknown>;
}

export interface UiNodeAttributes {
  node_type: "input" | "img" | "a" | "script" | "text" | "div";
  name: string;
  type?: string;
  value?: unknown;
  required?: boolean;
  disabled?: boolean;
  autocomplete?: string;
  pattern?: string;
  label?: UiText;
}

export interface UiNode {
  type: string;
  group: string;
  attributes: UiNodeAttributes;
  messages: UiText[];
  meta: { label?: UiText };
}

export interface OAuth2Client {
  client_id: string;
  client_name?: string;
  metadata?: { name?: string; subtitle?: string; headerImages?: string[]; slug?: string };
}

export interface Flow {
  id: string;
  type: "browser" | "api";
  ui: { action: string; method: string; nodes: UiNode[]; messages?: UiText[] };
  return_to?: string;
  state?: string;
  requested_aal?: "aal1" | "aal2";
  refresh?: boolean;
  // Login flows carry the whole request; Kratos 1.3's registration flows only the challenge.
  oauth2_login_challenge?: string;
  oauth2_login_request?: { challenge: string; client: OAuth2Client };
}

export interface Identity {
  id: string;
  traits: { email?: string; username?: string };
  verifiable_addresses?: { value: string; verified: boolean; via: string }[];
}

export interface Session {
  id: string;
  active: boolean;
  authenticator_assurance_level: "aal1" | "aal2";
  authentication_methods?: { method: string; aal?: string; completed_at?: string }[];
  identity: Identity;
}

export interface KratosErrorBody {
  error?: { id?: string; code?: number; message?: string; reason?: string };
  redirect_browser_to?: string;
  use_flow_id?: string;
}

/** What a Kratos answer means for the screen. */
export type Outcome =
  | { kind: "flow"; flow: Flow }
  | { kind: "redirect"; to: string }
  | { kind: "success"; body: SuccessBody }
  | { kind: "expired"; flowId?: string }
  | { kind: "error"; status: number; body: KratosErrorBody };

export interface SuccessBody {
  session?: Session;
  identity?: Identity;
  continue_with?: ContinueWith[];
}

export type ContinueWith =
  | { action: "show_verification_ui"; flow: { id: string; verifiable_address: string } }
  | { action: "redirect_browser_to"; redirect_browser_to: string }
  | { action: "set_ory_session_token" | "show_settings_ui" | "show_recovery_ui"; flow?: { id: string } };

async function call(url: string, init: RequestInit = {}): Promise<{ status: number; body: any }> {
  const res = await fetch(url, {
    ...init,
    credentials: "include",
    headers: { Accept: "application/json", ...(init.body ? { "Content-Type": "application/json" } : {}), ...init.headers },
  });
  const text = await res.text();
  let body: unknown = {};
  try {
    body = text ? JSON.parse(text) : {};
  } catch {
    body = { error: { message: text.slice(0, 200) } };
  }
  return { status: res.status, body };
}

function interpret({ status, body }: { status: number; body: any }): Outcome {
  // The page handles a missing second factor itself, so the person comes back to it afterwards.
  if (body?.error?.id === "session_aal2_required") return { kind: "error", status, body };
  if (body?.redirect_browser_to) return { kind: "redirect", to: body.redirect_browser_to };
  if (status === 200 && body?.ui) return { kind: "flow", flow: body };
  if (status === 200) return { kind: "success", body };
  if (status === 400 && body?.ui) return { kind: "flow", flow: body };
  if (status === 410) return { kind: "expired", flowId: body?.use_flow_id };
  return { kind: "error", status, body };
}

const query = (params: Record<string, string | undefined>) => {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) q.set(k, v);
  const s = q.toString();
  return s ? `?${s}` : "";
};

/**
 * Where a browser starts a flow itself. Kratos then answers with redirects a fetch can't follow, such as straight
 * back to Hydra when an existing session already covers an OAuth sign-in.
 */
export function browserFlowUrl(type: FlowType, params: Record<string, string | undefined> = {}): string {
  return `${config.kratosPublicUrl}/self-service/${type}/browser${query(params)}`;
}

export async function createFlow(type: FlowType, params: Record<string, string | undefined> = {}): Promise<Outcome> {
  return interpret(await call(`${config.kratosPublicUrl}/self-service/${type}/browser${query(params)}`));
}

export async function getFlow(type: FlowType, id: string): Promise<Outcome> {
  return interpret(await call(`${config.kratosPublicUrl}/self-service/${type}/flows${query({ id })}`));
}

export async function submitFlow(flow: Flow, body: Record<string, unknown>): Promise<Outcome> {
  return interpret(await call(flow.ui.action, { method: "POST", body: JSON.stringify(body) }));
}

/** The current session, or null when there is none (or it needs a second factor). */
export async function whoami(): Promise<Session | null> {
  const { status, body } = await call(`${config.kratosPublicUrl}/sessions/whoami`);
  return status === 200 ? (body as Session) : null;
}

export async function logoutUrl(returnTo?: string): Promise<string | null> {
  const { status, body } = await call(`${config.kratosPublicUrl}/self-service/logout/browser${query({ return_to: returnTo })}`);
  return status === 200 ? (body.logout_url as string) : null;
}

export async function getFlowError(id: string): Promise<KratosErrorBody["error"] | null> {
  const { status, body } = await call(`${config.kratosPublicUrl}/self-service/errors${query({ id })}`);
  return status === 200 ? body.error : null;
}

/** The first browser redirect a successful submission asks for. */
export function redirectFrom(body: SuccessBody): string | undefined {
  for (const c of body.continue_with ?? []) if (c.action === "redirect_browser_to") return c.redirect_browser_to;
  return undefined;
}

export function verificationFrom(body: SuccessBody): string | undefined {
  for (const c of body.continue_with ?? []) if (c.action === "show_verification_ui") return c.flow.id;
  return undefined;
}
